import { useState, useEffect } from 'react';

import { RolePermissionsPanel } from './components/RolePermissionsPanel';
import { RolesSidebar } from './components/RolesSidebar';
import { useCreateRole } from '@/api/mutations/useCreateRole';
import { useGetRoles, Role } from '@/api/queries/useGetRoles';

export const Roles = () => {
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [editedName, setEditedName] = useState('');
  const [editedPermissions, setEditedPermissions] = useState<string[]>([]);

  const { data: roles = [], isLoading } = useGetRoles();
  const { mutate: createRole, isLoading: isCreating } = useCreateRole();

  const selectRole = (role: Role | null) => {
    setSelectedRole(role);
    if (role) {
      setEditedName(role.name);
      setEditedPermissions(role.permissions);
    } else {
      setEditedName('');
      setEditedPermissions([]);
    }
  };

  // Auto-select the admin role when roles are loaded
  useEffect(() => {
    if (roles.length > 0 && !selectedRole) {
      const adminRole = roles.find((r) => r.name === 'Administrator');
      selectRole(adminRole ?? roles[0]);
    }
  }, [roles, selectedRole]);

  // Update selectedRole when roles are updated
  useEffect(() => {
    if (selectedRole && roles.length > 0) {
      const updatedRole = roles.find((r) => r.id === selectedRole.id);
      if (updatedRole && updatedRole !== selectedRole) {
        selectRole(updatedRole);
      }
    }
  }, [roles, selectedRole]);

  const handleRoleDeleted = () => {
    if (roles.length > 1) {
      const remainingRoles = roles.filter((r) => r.id !== selectedRole?.id);
      selectRole(remainingRoles[0] || null);
    } else {
      selectRole(null);
    }
  };

  const handleCreateRole = () => {
    createRole(
      {
        name: 'New Role',
        permissions: [],
      },
      {
        onSuccess: (newRole) => {
          selectRole(newRole);
        },
      },
    );
  };

  return (
    <div className="w-full overflow-hidden rounded-xl bg-white shadow-sm">
      <div className="flex min-h-[600px]">
        <RolesSidebar
          roles={roles}
          selectedRole={selectedRole}
          onSelectRole={selectRole}
          onCreateRole={handleCreateRole}
          isLoading={isLoading}
          isCreating={isCreating}
        />

        <RolePermissionsPanel
          role={selectedRole}
          editedName={editedName}
          editedPermissions={editedPermissions}
          onEditedNameChange={setEditedName}
          onEditedPermissionsChange={setEditedPermissions}
          onRoleDeleted={handleRoleDeleted}
        />
      </div>
    </div>
  );
};
